import type * as React from "react";

import { markShape } from "./Swipe";
import { cn } from "../lib/cn";

/**
 * Which ink the mark is written in.
 *
 * Deliberately not the status tones: a state that has a highlighter is a
 * `Swipe`, and offering `working`/`flag`/`stopped`/`brian` here would be a way
 * to say a status without its wash.
 */
export type StatusMarkTone = "neutral" | "accent";

const toneInk: Record<StatusMarkTone, string> = {
  neutral: "text-annotation",
  // accent-deep is the small-text indigo: this label is 10px on parchment.
  accent: "text-accent-deep",
};

export interface StatusMarkProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  "children"
> {
  /** neutral = a quiet aside (the default), accent = Brian's own indigo. */
  tone?: StatusMarkTone;
  /** Decorative glyph before the word, aligned exactly as `Swipe`'s is. */
  icon?: React.ReactNode;
  /** The word. A mark with no word is not a mark. */
  children: React.ReactNode;
  ref?: React.Ref<HTMLSpanElement>;
}

/**
 * The unmarked half of the Swipe family: a state written in small caps with no
 * highlighter over it, because there is no status to highlight. "Nobody
 * eligible" is not working, flagged, stopped or Brian's doing - it is a note
 * about why nothing happened, so it gets the shape of a status mark and none
 * of the marker ink.
 *
 * Reach for it only as the toneless branch beside a `Swipe`. If the state maps
 * to one of the four highlighters, use `Swipe` - the wash rule is what keeps
 * status from resting on colour alone, and this component must not become the
 * way around it. If the label states a fact rather than a state (a species, a
 * record type), that is a `Tag`.
 *
 * Unlike `Swipe`, which inherits its type so it can mark a heading or a line of
 * body copy, a StatusMark is always the small-caps annotation: it exists for
 * exactly one size of label, so the type is baked in.
 */
export function StatusMark({
  tone = "neutral",
  icon,
  children,
  className,
  ...rest
}: StatusMarkProps) {
  return (
    <span
      className={cn(
        markShape.row,
        "text-annotation-caps",
        toneInk[tone],
        className,
      )}
      {...rest}
    >
      {icon != null && (
        <span aria-hidden="true" className={markShape.icon}>
          {icon}
        </span>
      )}
      {children}
    </span>
  );
}
